import React, { Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { Link, BrowserRouter as Router, useLocation } from 'react-router-dom';
import {
  createTheme,
  ThemeProvider,
  responsiveFontSizes,
  styled
} from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { selectDarkMode, changeDarkMode } from './redux/homeSlice';
import { orange, red, deepOrange, blueGrey } from '@mui/material/colors';
import AppRoutes from './app/routes';
import DeviceCommands from './components/DeviceCommands';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import {
  selectDeviceType,
  selectSocketConnected,
  selectVisualEffects
} from './redux/settingsSlice';
import { selectChairperson, selectJudge } from './redux/workSlice';
import { DEVICE } from './constants/deviceType';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Slide from '@mui/material/Slide';
import { Avatar } from '@mui/material';
import ROUTES from './constants/routes';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import {
  selectActiveCompetition,
  selectTimetableForHall
} from './redux/timetableSlice';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TimetableTableList from './screens/Systems/Components/TimetableTableList';
import { useTranslation } from 'react-i18next';
import UpdatePrompt from './components/UpdatePrompt';
import EdgeSwipeGuard from './components/EdgeSwipeGuard';

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff'
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`
      },
      '& + .MuiSwitch-track': {
        opacity: 1
        // backgroundColor: theme.palette.mode !== "dark" ? "#8796A5" : "#aab4be",
      }
    }
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.mode !== 'dark' ? blueGrey[700] : '#001e3c',
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        '#fff'
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`
    }
  },
  '& .MuiSwitch-track': {
    // opacity: 1,
    // backgroundColor: theme.palette.mode !== "dark" ? "#8796A5" : "#aab4be",
    borderRadius: 20 / 2
  }
}));
interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
  children: React.ReactElement;
}
function HideOnScroll(props: Props) {
  const { children, window } = props;
  
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    threshold: 0.1, // Hide AppBar after scrolling down 100px
    disableHysteresis: true, // Remove the delay in both directions
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}
/**
 * Routes that should display in full-screen mode without AppBar
 */
const FULL_SCREEN_ROUTES: string[] = [ROUTES.LED_SCREEN, ROUTES.BROADCAST_SCREEN];

/**
 * Hook to determine if current route should be full-screen (no AppBar)
 */
const useIsFullScreenRoute = () => {
  const location = useLocation();
  return FULL_SCREEN_ROUTES.includes(location.pathname);
};

/**
 * AppContent - Extracted as a separate component to prevent remounting on parent re-renders
 * This is critical for preventing child component remounts when App's Redux subscriptions update
 */
interface AppContentProps {
  props: object;
  scrollRef: React.RefObject<HTMLDivElement>;
  scrollToBottom: () => void;
  appBarRef: React.RefObject<HTMLDivElement>;
  appBarHeight: number;
  darkState: boolean;
  handleThemeChange: () => void;
  storedJudge: ReturnType<typeof selectJudge> | null;
  activeCompetition: ReturnType<typeof selectActiveCompetition> | null;
  isSocketConnected: boolean;
  timetable: ReturnType<typeof selectTimetableForHall>;
  timetableDialogOpen: boolean;
  setTimetableDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppContent: React.FC<AppContentProps> = React.memo(function AppContent({
  props,
  scrollRef,
  scrollToBottom,
  appBarRef,
  appBarHeight,
  darkState,
  handleThemeChange,
  storedJudge,
  activeCompetition,
  isSocketConnected,
  timetable,
  timetableDialogOpen,
  setTimetableDialogOpen,
}) {
  const { t } = useTranslation();
  const isFullScreen = useIsFullScreenRoute();
  // On a chairperson device the person in the app bar is the chairperson who
  // signed in, never a judge left in the store from the phone's earlier life.
  const deviceType = useAppSelector(selectDeviceType);
  const signedInChairperson = useAppSelector(selectChairperson);
  const chairperson =
    deviceType === DEVICE.CHAIRPERSON ? signedInChairperson : null;

  // For full-screen routes (LED/Broadcast), render without AppBar
  if (isFullScreen) {
    return (
      <Box component="main" sx={{ height: 'calc(var(--vh) * 100)', width: '100vw', overflow: 'hidden' }}>
        <AppRoutes
          scrollRef={scrollRef}
          scrollToBottom={scrollToBottom}
          appBarHeight={0}
        />
      </Box>
    );
  }

  // Normal routes with AppBar
  return (
    <div ref={scrollRef}>
      <CssBaseline />
      <HideOnScroll {...props}>
        {/* An installed PWA renders under the system UI (viewport-fit=cover +
            black-translucent), so the bar pads itself past the status bar /
            Dynamic Island. appBarHeight is measured from the DOM, so every
            screen's `calc(var(--vh) * N - appBarHeight)` follows along. */}
        <AppBar
          component="nav"
          ref={appBarRef}
          sx={{ pt: 'env(safe-area-inset-top, 0px)' }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters variant="dense">
              <Box sx={{ flexGrow: 1, justifyContent: 'flex-start' }}>
                <Stack
                  direction={'row'}
                  spacing={1}
                  sx={{ justifyContent: 'center' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      padding: 0,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Link to={ROUTES.HOME} replace={true}>
                      <img
                        // Absolute: a document-relative src resolves against
                        // the current route, so on a deep link such as
                        // /training/:system/:round it would be fetched from
                        // /training/:system/android-chrome-512x512.png.
                        src={process.env.PUBLIC_URL + '/android-chrome-512x512.png'}
                        alt="marX"
                        width="40em"
                        height="40em"
                        style={{
                          padding: 0,
                          display: 'block',
                          margin: 'auto'
                        }}
                      />
                    </Link>
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                    {chairperson && (
                      <Typography
                        variant="h6"
                        noWrap
                        sx={{ textAlign: 'center' }}
                        key={chairperson.uuid}
                      >
                        {chairperson.name}
                      </Typography>
                    )}
                    {!chairperson && storedJudge && storedJudge?.uuid !== '' && (
                      <Stack
                        direction={'row'}
                        spacing={1}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <Avatar
                          variant="square"
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: darkState ? 'white' : 'black'
                          }}
                        >
                          {storedJudge.letter}
                        </Avatar>
                        <Typography
                          variant="h6"
                          sx={{ textAlign: 'center' }}
                          key={storedJudge.uuid}
                        >
                          {storedJudge.name}
                        </Typography>
                      </Stack>
                    )}
                    {!chairperson &&
                      storedJudge &&
                      storedJudge?.uuid === '' &&
                      activeCompetition && (
                        <Stack
                          direction={'row'}
                          spacing={1}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            maxWidth: '100%',
                            minHeight: 0,
                            overflow: 'hidden'
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ 
                              textAlign: 'center',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.2,
                              maxHeight: '2.4em',
                              maxWidth: '100%'
                            }}
                          >
                            {activeCompetition?.competitionName}
                            <Box
                              component="span"
                              sx={{ 
                                display: { xs: 'none', sm: 'inline' }
                              }}
                            >
                              {' | '}{activeCompetition?.dates}
                            </Box>
                          </Typography>
                        </Stack>
                      )}
                  </Box>
                </Stack>
              </Box>
              <Box sx={{ flexGrow: 0 }}>
                <Stack direction="row" sx={{ alignItems: 'center' }}>
                  {!isSocketConnected && (
                    <Alert severity="warning" variant="filled">
                      <WifiOffIcon />
                    </Alert>
                  )}
                  <IconButton
                    color="inherit"
                    onClick={() => setTimetableDialogOpen(true)}
                  >
                    <PendingActionsIcon />
                  </IconButton>
                  <MaterialUISwitch
                    checked={darkState}
                    onChange={handleThemeChange}
                  />
                </Stack>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      <Dialog
        fullScreen
        open={timetableDialogOpen}
        onClose={() => setTimetableDialogOpen(false)}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setTimetableDialogOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6">
              {t('timetable.timetable')}
            </Typography>
          </Toolbar>
        </AppBar>
        <TimetableTableList
          timetable={timetable}
          selectedItem={null}
          handleRowClick={null}
          t={t}
          // A chairperson's timetable dims the categories somebody else
          // chairs; a judge's highlights the rounds they are on.
          currentAdjudicator={chairperson ? undefined : (storedJudge ?? undefined)}
          chairpersonUuid={chairperson?.uuid}
        />
      </Dialog>
      <Box
        sx={{ 
          pt: `${appBarHeight}px`,
          pb: 2,
        }}
        component="main"
      >
        <AppRoutes
          scrollRef={scrollRef}
          scrollToBottom={scrollToBottom}
          appBarHeight={appBarHeight}
        />
        <div ref={scrollRef} />
      </Box>
    </div>
  );
});

const App: React.FC = (props) => {
  const [darkState, setDarkState] = useState(false);
  const [timetableDialogOpen, setTimetableDialogOpen] = useState(false);
  const [appBarHeight, setAppBarHeight] = useState(50); // Default fallback height
  const scrollRef = useRef<HTMLDivElement>(null);
  const appBarRef = useRef<HTMLDivElement>(null);

  // Memoize scrollToBottom to prevent AppContent re-renders
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }, []);

  // Memoize handleThemeChange to prevent AppContent re-renders
  const handleThemeChange = useCallback(() => {
    setDarkState(prev => !prev);
  }, []);

  const darkMode = useAppSelector(selectDarkMode);
  const visualEffects = useAppSelector(selectVisualEffects);

  const palletType = darkMode ? 'dark' : 'light';
  const mainPrimaryColor = darkMode ? orange[500] : blueGrey[500];
  const mainSecondaryColor = darkMode ? deepOrange[900] : red[500];

  const visualSettings = visualEffects
    ? {}
    : {
        // Name of the component ⚛️
        MuiButtonBase: {
          defaultProps: {
            // The props to apply
            disableRipple: true // No more ripple, on the whole application 💣!
          }
        },
        MuiCssBaseline: {
          styleOverrides: {
            '*, *::before, *::after': {
              transition: 'none !important',
              animation: 'none !important'
            }
          }
        }
      };

  let darkTheme = createTheme({
    palette: {
      mode: palletType,
      primary: {
        main: mainPrimaryColor
      },
      secondary: {
        main: mainSecondaryColor
      },
      contrastThreshold: 3
    },
    components: visualSettings
  });

  darkTheme = responsiveFontSizes(darkTheme);
  const isSocketConnected = useAppSelector(selectSocketConnected);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (typeof darkState === 'undefined') {
      setDarkState(false);
    }
    setDarkState(darkMode ? true : false);
  }, []);
  useEffect(() => {
    dispatch(changeDarkMode(darkState));
  }, [darkState]);
  const storedJudge = useAppSelector(selectJudge);

  // Added reconnection strategy for socket disconnection
  useEffect(() => {
    if (!isSocketConnected) {
      // console.warn('Socket disconnected. Attempting to reconnect...');
      // Implement reconnection logic here, e.g., retry connection after a delay
      const reconnectInterval = setInterval(() => {
        if (!isSocketConnected) {
          console.info('Retrying socket connection...');
          // Trigger reconnection logic (e.g., reinitialize socket)
        } else {
          clearInterval(reconnectInterval);
        }
      }, 5000); // Retry every 5 seconds

      return () => clearInterval(reconnectInterval);
    }
  }, [isSocketConnected]);
  
  // Measure AppBar height with ResizeObserver and orientation change detection
  useEffect(() => {
    const measureAppBarHeight = () => {
      if (appBarRef.current) {
        const height = appBarRef.current.getBoundingClientRect().height;
        setAppBarHeight(height);
        // console.log('📏 AppBar height measured:', height + 'px');
      }
    };

    let resizeObserver: ResizeObserver;
    
    if (appBarRef.current) {
      // Use ResizeObserver for precise height tracking
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // Border box, not contentRect: the bar pads itself by the top
          // safe-area inset, and screens offset content by this value.
          const height = (entry.target as HTMLElement).getBoundingClientRect()
            .height;
          setAppBarHeight(height);
          // console.log('📏 AppBar height updated (ResizeObserver):', height + 'px');
        }
      });
      
      resizeObserver.observe(appBarRef.current);
    }

    // Handle orientation changes
    const handleOrientationChange = () => {
      // console.log('🔄 Orientation changed, re-measuring AppBar height...');
      // Delay measurement to ensure layout has settled after orientation change
      setTimeout(measureAppBarHeight, 200);
    };

    // Fallback: measure on mount, resize, and content changes
    measureAppBarHeight();
    window.addEventListener('resize', measureAppBarHeight);
    
    // Listen for orientation changes (modern approach)
    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleOrientationChange);
    }
    
    // Fallback for older browsers
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Also measure when theme changes (dark/light mode might affect height)
    const timeoutId = setTimeout(measureAppBarHeight, 100);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', measureAppBarHeight);
      
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleOrientationChange);
      }
      window.removeEventListener('orientationchange', handleOrientationChange);
      
      clearTimeout(timeoutId);
    };
  }, [darkState, storedJudge]); // Re-measure when content changes

  // Nothing here forces body/#root to `height: auto; overflow: visible` any
  // more. That override handed scrolling to the document, which is the only
  // scroller iOS offers pull-to-refresh on. #root is the scroller again, as
  // index.css describes — touch scrolling inside it works now that iNoBounce
  // is gone (it was vetoing every touchmove it didn't recognise).

  // Get timetable data for TimetableTable
  const timetable = useAppSelector(selectTimetableForHall);
  const activeCompetition = useAppSelector(selectActiveCompetition);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline enableColorScheme />
      <EdgeSwipeGuard />
      <UpdatePrompt />
      <Router>
        <DeviceCommands />
        <Suspense fallback={<div>Loading...</div>}>
          <AppContent
            props={props}
            scrollRef={scrollRef}
            scrollToBottom={scrollToBottom}
            appBarRef={appBarRef}
            appBarHeight={appBarHeight}
            darkState={darkState}
            handleThemeChange={handleThemeChange}
            storedJudge={storedJudge}
            activeCompetition={activeCompetition}
            isSocketConnected={isSocketConnected}
            timetable={timetable}
            timetableDialogOpen={timetableDialogOpen}
            setTimetableDialogOpen={setTimetableDialogOpen}
          />
        </Suspense>
      </Router>
    </ThemeProvider>
  );
};

export default App;
